// Context menu for chat items: pin, rename, move to folder, download,
// debug export, delete.

import React, { useEffect, useRef, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import type { ChatFolder } from '../../types';
import { getActiveZoom } from '../../lib/zoomPosition';

interface ChatItemMenuProps {
  sessionId: string;
  sessionFolderId?: string;
  anchorRect: DOMRect;
  folders: ChatFolder[];
  pinned?: boolean;
  /** Omitted for temporary chats, which can't be pinned. */
  onTogglePin?: () => void;
  onRename: () => void;
  onMoveToFolder: (folderId: string | undefined) => void;
  onDownloadPlain: () => void;
  onDownloadDetailed: () => void;
  onExportDebug: () => void;
  onDelete: () => void;
  onClose: () => void;
}

export default function ChatItemMenu({
  sessionFolderId,
  anchorRect,
  folders,
  pinned,
  onTogglePin,
  onRename,
  onMoveToFolder,
  onDownloadPlain,
  onDownloadDetailed,
  onExportDebug,
  onDelete,
  onClose,
}: ChatItemMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  const [subMenuOpen, setSubMenuOpen] = useState(false);
  const [subMenuFlip, setSubMenuFlip] = useState(false);

  // Position (unscaled CSS px; see getActiveZoom)
  const z = getActiveZoom();
  const top = anchorRect.bottom / z + 4;
  const left = Math.min(anchorRect.left / z, window.innerWidth / z - 200);

  // Close on click outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  const openSubMenu = useCallback((el: HTMLElement) => {
    setSubMenuOpen(true);
    // Check if submenu would overflow right edge (in unscaled CSS px)
    const zHover = getActiveZoom();
    const rect = el.getBoundingClientRect();
    setSubMenuFlip(rect.right / zHover + 180 > window.innerWidth / zHover);
  }, []);

  const handleSubMenuHover = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    openSubMenu(e.currentTarget);
  }, [openSubMenu]);

  // Focus the first item so keyboard users can act right away.
  useEffect(() => {
    menuRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
  }, []);

  // Arrow-key navigation between the visible menu items.
  const handleMenuKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    const items = Array.from(
      menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [],
    );
    if (items.length === 0) return;
    e.preventDefault();
    const i = items.indexOf(document.activeElement as HTMLElement);
    const next = e.key === 'ArrowDown' ? (i + 1) % items.length : (i - 1 + items.length) % items.length;
    items[next].focus();
  }, []);

  return ReactDOM.createPortal(
    <div
      ref={menuRef}
      className="malpedia-context-menu"
      style={{ top, left }}
      role="menu"
      onKeyDown={handleMenuKeyDown}
    >
      {onTogglePin && (
        <button
          className="malpedia-context-menu-item"
          role="menuitem"
          onClick={() => { onTogglePin(); onClose(); }}
        >
          {pinned ? 'Unpin' : 'Pin to top'}
        </button>
      )}
      <button
        className="malpedia-context-menu-item"
        role="menuitem"
        onClick={() => { onRename(); onClose(); }}
      >
        Rename
      </button>

      <div
        className="malpedia-context-menu-item malpedia-context-menu-submenu-trigger"
        role="menuitem"
        tabIndex={-1}
        aria-haspopup="menu"
        aria-expanded={subMenuOpen}
        onMouseEnter={handleSubMenuHover}
        onMouseLeave={() => setSubMenuOpen(false)}
        onClick={(e) => {
          if (e.target === e.currentTarget || !(e.target as HTMLElement).closest('.malpedia-context-submenu')) {
            if (subMenuOpen) setSubMenuOpen(false);
            else openSubMenu(e.currentTarget);
          }
        }}
        onKeyDown={(e) => {
          if (e.target !== e.currentTarget) return;
          if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowRight') {
            e.preventDefault();
            openSubMenu(e.currentTarget);
            // Focus the first folder once the submenu has rendered.
            const trigger = e.currentTarget;
            requestAnimationFrame(() => {
              trigger.querySelector<HTMLElement>('.malpedia-context-submenu [role="menuitem"]')?.focus();
            });
          }
        }}
      >
        <span>Move to folder</span>
        <span className="malpedia-context-menu-arrow">▶</span>
        {subMenuOpen && (
          <div
            className={`malpedia-context-menu malpedia-context-submenu${subMenuFlip ? ' flip' : ''}`}
            role="menu"
          >
            {folders.map((f) => (
              <button
                key={f.id}
                className={`malpedia-context-menu-item${sessionFolderId === f.id ? ' current' : ''}`}
                role="menuitem"
                onClick={() => { onMoveToFolder(f.id); onClose(); }}
              >
                📂 {f.name}
                {sessionFolderId === f.id && <span className="malpedia-context-menu-check">✓</span>}
              </button>
            ))}
            {folders.length > 0 && <div className="malpedia-context-menu-divider" />}
            <button
              className={`malpedia-context-menu-item${!sessionFolderId ? ' current' : ''}`}
              role="menuitem"
              onClick={() => { onMoveToFolder(undefined); onClose(); }}
            >
              Unfiled
              {!sessionFolderId && <span className="malpedia-context-menu-check">✓</span>}
            </button>
          </div>
        )}
      </div>

      <div className="malpedia-context-menu-divider" />

      <button
        className="malpedia-context-menu-item"
        role="menuitem"
        onClick={() => { onDownloadPlain(); onClose(); }}
      >
        Download (Plain)
      </button>
      <button
        className="malpedia-context-menu-item"
        role="menuitem"
        onClick={() => { onDownloadDetailed(); onClose(); }}
      >
        Download (Detailed)
      </button>
      <button
        className="malpedia-context-menu-item"
        role="menuitem"
        onClick={() => { onExportDebug(); onClose(); }}
      >
        Export debug info
      </button>

      <div className="malpedia-context-menu-divider" />

      <button
        className="malpedia-context-menu-item malpedia-context-menu-item--danger"
        role="menuitem"
        onClick={() => { onDelete(); onClose(); }}
      >
        Delete
      </button>
    </div>,
    document.body,
  );
}
